import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Package, Plus, X } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { getErrorMessage } from '@/lib/api'
import { assetsApi } from '@/services/assets'

interface OrderItemAssetsProps {
  orderItemId?: string | number | null
  customerUserId?: string | number | null
  serviceInstanceId?: string | number | null
  canEdit: boolean
}

export function OrderItemAssets({
  orderItemId,
  customerUserId,
  canEdit,
}: OrderItemAssetsProps) {
  const queryClient = useQueryClient()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<number[]>([])
  const hasOrderItem = Boolean(orderItemId && String(orderItemId).trim())

  const linksQuery = useQuery({
    queryKey: ['order-item-assets', orderItemId],
    queryFn: () => assetsApi.listOrderItemAssets(orderItemId!),
    enabled: hasOrderItem,
  })

  const assetsQuery = useQuery({
    queryKey: ['order-item-asset-picker', customerUserId, search],
    queryFn: async () => {
      if (customerUserId) {
        const rows = await assetsApi.listCustomerAssets(customerUserId)
        const term = search.trim().toLowerCase()
        return term
          ? rows.filter(
              (row) =>
                row.name.toLowerCase().includes(term) ||
                row.asset_code.toLowerCase().includes(term) ||
                (row.serial_number || '').toLowerCase().includes(term),
            )
          : rows
      }
      const page = await assetsApi.list({ search: search || undefined, page_size: 30 })
      return page.results
    },
    enabled: pickerOpen,
  })

  const linkMutation = useMutation({
    mutationFn: () =>
      assetsApi.linkOrderItemAssets({
        order_item: orderItemId!,
        asset_ids: selected,
        replace: true,
      }),
    onSuccess: async () => {
      toast.success('Assets linked')
      setPickerOpen(false)
      await queryClient.invalidateQueries({ queryKey: ['order-item-assets', orderItemId] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const linked = linksQuery.data ?? []
  const pickerAssets = assetsQuery.data ?? []
  const linkedIds = useMemo(() => new Set(linked.map((row) => row.asset)), [linked])

  const openPicker = () => {
    if (!customerUserId) {
      toast.message('Select a customer to link assets')
      return
    }
    setSelected(Array.from(linkedIds))
    setPickerOpen(true)
  }

  const toggle = (assetId: number) => {
    setSelected((prev) => (prev.includes(assetId) ? prev.filter((id) => id !== assetId) : [...prev, assetId]))
  }

  if (!hasOrderItem) {
    return null
  }

  return (
    <div className="space-y-2 rounded-md border border-dashed p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium">Assets</div>
        {canEdit ? (
          <Button size="sm" variant="outline" onClick={openPicker} disabled={!customerUserId}>
            <Plus className="mr-2 h-4 w-4" />
            Link assets
          </Button>
        ) : null}
      </div>
      {!customerUserId ? (
        <p className="text-xs text-muted-foreground">Select a customer to link assets.</p>
      ) : linksQuery.isLoading ? (
        <Skeleton className="h-8 w-full" />
      ) : linked.length === 0 ? (
        <p className="text-xs text-muted-foreground">No assets linked to this order item.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {linked.map((row) => (
            <Badge key={row.id} variant="secondary" className="gap-1">
              {row.asset_code} · {row.asset_name}
            </Badge>
          ))}
        </div>
      )}

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Link assets to order item</DialogTitle>
            <DialogDescription>Choose customer assets for this booking line.</DialogDescription>
          </DialogHeader>
          <Input placeholder="Search…" value={search} onChange={(event) => setSearch(event.target.value)} />
          <div className="max-h-72 space-y-2 overflow-y-auto">
            {assetsQuery.isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : pickerAssets.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Package className="h-4 w-4" />
                No assets found for this customer.
              </div>
            ) : (
              pickerAssets.map((asset) => (
                <label key={asset.id} className="flex cursor-pointer items-start gap-3 rounded-md border p-3">
                  <Checkbox checked={selected.includes(asset.id)} onCheckedChange={() => toggle(asset.id)} />
                  <div>
                    <div className="text-sm font-medium">
                      {asset.asset_code} · {asset.name}
                    </div>
                    <div className="text-xs capitalize text-muted-foreground">{asset.asset_class}</div>
                  </div>
                </label>
              ))
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPickerOpen(false)}>
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button disabled={linkMutation.isPending} onClick={() => linkMutation.mutate()}>
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
